import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Hero from "../Components/Hero";
import ProductGrid from "../Components/ProductGrid";
import productsData from "../data/products.json";

// Helper function to map color names to hex codes
const getColorHex = (colorName) => {
  const colorMap = {
    'Black': '#000000',
    'Titanium Black': '#2C2C2E',
    'Titan Black': '#2C2C2E',
    'Graphite': '#3A3A3C',
    'White': '#FFFFFF',
    'Awesome White': '#FAFAFA',
    'Silver': '#C0C0C0',
    'Navy': '#1E3A8A',
    'Violet': '#8B5CF6',
    'Green': '#22C55E',
    'Mint': '#86EFAC',
    'Yellow': '#FCD34D',
    'Gold': '#F59E0B',
    'Pink Gold': '#E8B69A',
    'Titanium Pink Gold': '#E8B69A',
    'Titanium Gray': '#6B7280',
    'Titanium Silver Blue': '#60A5FA',
    'Titanium Jade Green': '#6EE7B7',
    'Titanium White Silver': '#E5E7EB',
    'Bora Purple': '#A855F7',
    'Iceblue': '#A5F3FC',
    'Awesome Iceblue': '#A5F3FC',
    'Light Blue': '#BFDBFE',
    'Lime': '#BEF264',
    'Lilac': '#C4B5FD',
    'Beige': '#F5E6D3',
  };
  return colorMap[colorName] || '#CCCCCC';
};

// Home Product Card with capacity selector
function HomeProductCard({ product, onWhatsAppOrder, navigate }) {
  const [selectedCapacity, setSelectedCapacity] = useState(
    product.availableSizes ? product.availableSizes[0] : null
  );

  const currentPrice = selectedCapacity ? selectedCapacity.price : product.price;
  const currentOriginalPrice = selectedCapacity ? selectedCapacity.originalPrice : product.originalPrice;
  const currentDiscount = selectedCapacity ? selectedCapacity.discount : product.discount;

  return (
    <div
      className="home-product-card"
      onClick={() => navigate(`/product/${product.id}`)}
      style={{ cursor: 'pointer' }}
    >
      <div className="home-product-image">
        <img src={product.colorImages?.[0] || product.image} alt={product.name} />
      </div>
      <h4>{product.name}</h4>

      {/* Capacity Selector - Hide for LEDs */}
      {product.availableSizes && product.availableSizes.length > 0 && product.category !== 'LEDs' && (
        <div className="capacity-selector" onClick={(e) => e.stopPropagation()}>
          <div className="capacity-options">
            {product.availableSizes.map((size, index) => (
              <button
                key={index}
                className={`capacity-btn ${selectedCapacity?.capacity === size.capacity ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCapacity(size);
                }}
              >
                {size.capacity}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Color Dots - Only show for non-LED products with color images */}
      {product.colors && product.colors.length > 0 && product.colorImages && product.category !== 'LEDs' && (
        <div className="product-card-colors">
          {product.colors.slice(0, Math.min(product.colorImages.length, 5)).map((color, index) => (
            <div
              key={index}
              className="color-dot"
              style={{
                backgroundColor: getColorHex(color),
                border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#FAFAFA' ? '1.5px solid #ddd' : '1.5px solid rgba(0, 0, 0, 0.15)'
              }}
              title={color}
            />
          ))}
          {product.colorImages.length > 5 && (
            <span style={{ fontSize: '0.75rem', color: '#666' }}>+{product.colorImages.length - 5}</span>
          )}
        </div>
      )}

      <div className="product-specs">
        {product.ram && <span className="spec">RAM: {product.ram}</span>}
        {product.storage && <span className="spec">Storage: {product.storage}</span>}
        {product.size && <span className="spec">Size: {product.size}</span>}
        {product.channels && <span className="spec">{product.channels}</span>}
        {product.type && !product.availableSizes && <span className="spec">{product.type}</span>}
      </div>

      <div className="product-price-section">
        <div className="price-info">
          <p className="current-price">PKR {currentPrice.toLocaleString()}</p>
          {currentDiscount > 0 && (
            <p className="original-price">PKR {currentOriginalPrice.toLocaleString()}</p>
          )}
        </div>
      </div>

      <button className="home-shop-btn" onClick={(e) => {
        e.stopPropagation();
        navigate(`/product/${product.id}`);
      }}>
        Shop Now
      </button>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();

  // WhatsApp order function
  const handleWhatsAppOrder = (product) => {
    const specs = [];
    if (product.ram) specs.push(`RAM: ${product.ram}`);
    if (product.storage) specs.push(`Storage: ${product.storage}`);
    if (product.size) specs.push(`Size: ${product.size}`);
    if (product.channels) specs.push(product.channels);
    if (product.type) specs.push(product.type);

    const specsText = specs.length > 0 ? `\nSpecs: ${specs.join(', ')}` : '';
    const discountText = product.discount > 0 ? `\nOriginal Price: PKR ${product.originalPrice.toLocaleString()}\nDiscount: ${product.discount}%` : '';

    const message = `Hi *Samsung Door Step*! 👋\n\nI'm interested in:\n\n📱 *${product.name}*${specsText}\n💰 Price: *PKR ${product.price.toLocaleString()}*${discountText}\n\nPlease provide more details.`;

    const whatsappURL = `https://wa.me/923292737737?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
  };

  // Get first 4 products from each category for home page preview
  const productsByCategory = {
    "Mobiles": productsData.mobiles.slice(0, 4),
    "LEDs": productsData.leds.slice(0, 4),
    "Home Appliances": productsData.homeAppliances.slice(0, 4),
    "Watches": productsData.watches.slice(0, 3),
    "Tablets": productsData.tablets.slice(0, 3),
    "Earbuds": productsData.earbuds.slice(0, 2),
    "Soundbars": productsData.soundbars.slice(0, 4),
    "Projectors": productsData.projectors.slice(0, 3)
  };

  return (
    <div className="home-page">
      <Hero />
      
      <section className="featured-section">
        <div className="container">
          <h2>Featured Products</h2>
          <p>Exclusive Samsung products delivered straight to your doorstep</p>
          <ProductGrid />
        </div>
      </section>

      {/* Products by Category */}
      <section className="home-products-section">
        <div className="container">
          {Object.entries(productsByCategory).map(([category, products]) => {
            // Map category names to URL parameters
            const categoryMap = {
              "Mobiles": "mobiles",
              "LEDs": "leds",
              "Home Appliances": "homeAppliances",
              "Watches": "watches",
              "Tablets": "tablets",
              "Earbuds": "earbuds",
              "Soundbars": "soundbars",
              "Projectors": "projectors"
            };
            const categoryParam = categoryMap[category] || "";

            return (
            <div key={category} className="home-category-block">
              <div className="home-category-header">
                <h3>{category}</h3>
                <Link to={`/products?category=${categoryParam}`} className="view-all-link">View All →</Link>
              </div>
              
              <div className="home-products-grid">
                {products.map((product) => (
                  <HomeProductCard
                    key={product.id}
                    product={product}
                    onWhatsAppOrder={handleWhatsAppOrder}
                    navigate={navigate}
                  />
                ))}
              </div>
            </div>
            );
          })}
        </div>
      </section>
      
      <section className="benefits-section">
        <div className="container">
          <h2>Why Choose Samsung Door-Step?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🚚</div>
              <h3>Fast Delivery</h3>
              <p>Same-day delivery available in major cities</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">✅</div>
              <h3>Authentic Products</h3>
              <p>100% genuine Samsung products with warranty</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3>Best Prices</h3>
              <p>Competitive pricing with exclusive discounts</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🛡️</div>
              <h3>Secure Payment</h3>
              <p>Safe and secure payment options available</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
